import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import LaptopImage from "@/assets/Group 1 3.svg";
import MobileImage from "@/assets/15.svg";
import Icons from "@/assets/Icons.png";
import ProductOverviewVideo from "@/assets/YoutubeVideo.svg";
import ScrollFrame1 from "@/assets/Frame.svg";
import ScrollFrame2 from "@/assets/Frame-2.svg";
import ScrollFrame3 from "@/assets/Frame-3.svg";
import ScrollFrame4 from "@/assets/Frame-4.svg";
import MainBG from "@/assets/Black and White Modern BG.png";
import BG2 from "@/assets/BG-slide-2.png";
import BG3 from "@/assets/BG-slide-3.png";
import BG4 from "@/assets/BG-slide-4.png";
import BG5 from "@/assets/Black and White Modern BG.png";
import BG6 from "@/assets/BG-slide-6.png";
import Testimonial1 from "@/assets/Testimonial-1.jpg";
import Testimonial2 from "@/assets/Testimonial-2.jpg";
import Testimonial3 from "@/assets/Testimonial-3.jpg";
import Testimonial4 from "@/assets/Testimonial-4.jpg";
import { useEffect, useRef, useState } from "react";
import { Star, Plus, Minus } from "lucide-react";
import { motion, Variants } from "framer-motion";

const slideInLeft: Variants = {
  hidden: { opacity: 0, x: -100 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 1.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const slideInRight: Variants = {
  hidden: { opacity: 0, x: 100 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 1.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const slideInBottom: Variants = {
  hidden: { opacity: 0, y: 80 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 1.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 1.6, ease: [0.16, 1, 0.3, 1] },
  },
};

const testimonialContainer: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.18, // ⬅ delay between cards
      delayChildren: 0.2, // ⬅ slight pause before first
    },
  },
};

const testimonialItem: Variants = {
  hidden: { opacity: 0, y: 50, rotateX: 8, filter: "blur(4px)" },
  visible: {
    opacity: 1,
    y: 0,
    rotateX: 0,
    filter: "blur(0px)",
    transition: { duration: 1.0, ease: [0.16, 1, 0.3, 1] },
  },
};

const faqs = [
  {
    question: "What is this platform used for?",
    answer:
      "This platform is designed for internal company communication, allowing teams to manage recipients, create message templates, and send targeted notifications through a centralized web and mobile system.",
  },
  {
    question: "Who can use this product within the organization?",
    answer:
      "The platform can be used by administrators, HR teams, managers, and internal communication teams across departments.",
  },
  {
    question: "Is the platform available on both web and mobile?",
    answer:
      "Yes, the platform is available on web and mobile, ensuring seamless access and communication anytime, anywhere.",
  },
];

const testimonials = [
  {
    name: "Anil Kumar",
    role: "Company CEO",
    image: Testimonial1,
    rating: 4,
    message:
      "This platform has greatly improved how we handle internal communication. Managing recipients and campaigns is now structured and efficient.",
  },
  {
    name: "Priya Nair",
    role: "HR Operations Manager",
    image: Testimonial2,
    rating: 5,
    message:
      "The dashboard gives complete visibility into internal messaging. Web and mobile access makes it extremely convenient for our teams.",
  },
  {
    name: "James D.",
    role: "Small Business Owner",
    image: Testimonial3,
    rating: 4,
    message:
      "Reusable templates and department-wise targeting have reduced manual effort and ensured consistent communication across teams.",
  },
  {
    name: "Ananya Sharma",
    role: "IT Administrator",
    image: Testimonial4,
    rating: 5,
    message:
      "A simple yet powerful solution for company-wide notifications. It fits perfectly into our internal processes.",
  },
];

const features = [
  {
    title: "Unified Communication Dashboard",
    description:
      "Get a complete, real-time view of internal communications. Monitor recipients, campaigns, and templates from a single dashboard designed for clarity, control, and confident decision-making.",
    image: ScrollFrame1,
  },
  {
    title: "Targeted Message Composition",
    description:
      "Create and send internal messages through a guided workflow. Select recipients, compose messages using templates, review details, and deliver communications with confidence.",
    image: ScrollFrame2,
  },
  {
    title: "Structured Recipient Management",
    description:
      "Maintain a centralized directory of employees and teams. Organize recipients by department and zone to ensure accurate targeting and controlled internal communication.",
    image: ScrollFrame3,
  },
  {
    title: "Smart Message Templates",
    description:
      "Create and manage reusable message templates for email, SMS, and WhatsApp. Ensure consistency and speed across teams.",
    image: ScrollFrame4,
  },
];

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section
      className="py-16 md:py-20 bg-background"
      style={{
        backgroundImage: `url(${BG2})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="container mx-auto px-5 sm:px-8 lg:px-12 max-w-4xl">
        <h2 className="text-3xl md:text-4xl font-bold mb-3 text-center">
          Frequently asked questions
        </h2>
        <p className="text-muted-foreground text-center mb-10">
          Everything you need to know about the product
        </p>

        <div className="space-y-3">
          {faqs.map((item, index) => {
            const isOpen = openIndex === index;

            return (
              <div
                key={index}
                className="border-b border-border py-5 cursor-pointer"
                onClick={() => setOpenIndex(isOpen ? null : index)}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-lg">{item.question}</span>

                  <div className="h-7 w-7 flex items-center justify-center rounded-full border border-primary text-primary shrink-0">
                    {isOpen ? (
                      <Minus className="h-4 w-4" />
                    ) : (
                      <Plus className="h-4 w-4" />
                    )}
                  </div>
                </div>

                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isOpen ? "max-h-40 mt-4" : "max-h-0"
                  }`}
                >
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {item.answer}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const CARD_WIDTH_DESKTOP = 750; // Active desktop size
const CARD_HEIGHT_DESKTOP = 535; // Fixed height
const CARD_WIDTH_TABLET = 520;
const CARD_WIDTH_MOBILE = 340;
const GAP = 24;

const FeatureCard = ({ title, description, image, active }) => {
  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
  const isTablet =
    typeof window !== "undefined" &&
    window.innerWidth >= 640 &&
    window.innerWidth < 1024;

  const width = isMobile
    ? CARD_WIDTH_MOBILE
    : isTablet
      ? CARD_WIDTH_TABLET
      : CARD_WIDTH_DESKTOP;

  const height = !isMobile && !isTablet ? CARD_HEIGHT_DESKTOP : "auto";

  return (
    <div
      style={{
        width: width,
        height: height,
      }}
      className={`
        flex-shrink-0 rounded-2xl overflow-hidden border
        transition-all duration-500 ease-in-out
        ${
          active
            ? "bg-white shadow-2xl z-20 scale-100 opacity-100"
            : "bg-white/90 scale-90 opacity-60"
        }
      `}
    >
      <div className="w-full bg-black">
        <img
          src={image}
          alt={title}
          className="w-full object-contain select-none"
          draggable={false}
        />
      </div>

      <div className="p-6 lg:p-8">
        <h4 className="text-xl lg:text-2xl font-semibold text-brand mb-3">
          {title}
        </h4>
        <p className="text-base text-muted-foreground leading-relaxed">
          {description}
        </p>
      </div>
    </div>
  );
};

const StarRating = ({ rating }: { rating: number }) => {
  return (
    <div className="flex justify-center gap-1 mb-4">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${
            i < rating ? "fill-secondary text-secondary" : "text-gray-300"
          }`}
        />
      ))}
    </div>
  );
};

const TestimonialCard = ({
  name,
  role,
  image,
  rating,
  message,
}: {
  name: string;
  role: string;
  image: string;
  rating: number;
  message: string;
}) => {
  return (
    <motion.div
      variants={testimonialItem}
      className="relative bg-white text-black rounded-3xl px-5 sm:px-6 pt-14 sm:pt-16 pb-6 sm:pb-8 text-center shadow-xl"
    >
      <div className="absolute -top-9 sm:-top-10 left-1/2 -translate-x-1/2">
        <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full border-4 border-white overflow-hidden shadow-lg">
          <img src={image} alt={name} className="h-full w-full object-cover" />
        </div>
      </div>

      <StarRating rating={rating} />

      <p className="text-sm text-muted-foreground leading-relaxed mb-5 sm:mb-6">
        “{message}”
      </p>

      <div className="font-semibold">{name}</div>
      <div className="text-xs text-muted-foreground mt-1">{role}</div>
    </motion.div>
  );
};
const CommunicationToolDetails = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const totalCards = features.length;

  const nextSlide = () => {
    setActiveIndex((prev) => (prev + 1) % totalCards);
  };

  const prevSlide = () => {
    setActiveIndex((prev) => (prev === 0 ? totalCards - 1 : prev - 1));
  };
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     nextSlide();
  //   }, 5000);

  //   return () => clearInterval(interval);
  // }, []);

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const handleTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    const distance = touchStartX.current - touchEndX.current;

    if (distance > 50) nextSlide();
    if (distance < -50) prevSlide();
  };
  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      {/* ================= HERO ================= */}

      <section
        className="relative bg-black text-white pt-28 md:pt-40 pb-64 md:pb-64 overflow-hidden text-center"
        style={{
          backgroundImage: `url(${MainBG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container mx-auto px-6 relative z-10 max-w-5xl">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-tight mb-6 tracking-tight">
            {/* Empower people the <span className="text-brand">smarter</span>{" "}
            way */}
            Smart Internal <span className="text-brand">Communication,</span>{" "}
            Simplified
          </h1>

          <p className="text-lg sm:text-xl md:text-2xl text-white/80 max-w-4xl mx-auto mb-10 md:mb-12 font-light">
            A centralized platform to manage recipients, create message
            templates, and run internal notification campaigns with ease.
          </p>

          <Button
            size="lg"
            className="bg-brand hover:bg-brand-hover text-base sm:text-lg md:text-xl px-8 sm:px-10 md:px-12 py-6 md:py-7 shadow-lg shadow-primary/30 transition-all"
            asChild
          >
            <Link to="/contact" className="flex items-center gap-3">
              Request Demo <ArrowRight className="h-5 w-5 md:h-6 md:w-6" />
            </Link>
          </Button>
        </div>
      </section>

      <section>
        <div className="flex items-center justify-center -mt-70 md:-mt-60 px-4 relative z-20">
          {/* Laptop */}
          <motion.img
            src={LaptopImage}
            alt="Dashboard Laptop"
            className="relative z-10 w-[80%] max-w-[1200px]"
            variants={slideInLeft}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.4 }}
          />

          {/* Mobile */}
          <motion.img
            src={MobileImage}
            alt="Dashboard Mobile"
            className="absolute z-20
                 w-28 xs:w-32 sm:w-40 lg:w-96
                 right-4 sm:right-8 lg:right-[12%]
                 bottom-4 sm:bottom-8 lg:bottom-[18%]"
            initial={{ opacity: 0, scale: 0.9, x: -30 }}
            whileInView={{ opacity: 1, scale: 1, x: 0 }}
            transition={{
              delay: 0.4,
              duration: 1.2,
              ease: [0.16, 1, 0.3, 1],
            }}
            viewport={{ once: false, amount: 0.4 }}
          />
        </div>
      </section>

      {/* ================= INTRO ================= */}
      <section
        className="py-16 md:py-20 bg-background"
        style={{
          backgroundImage: `url(${BG3})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="container mx-auto px-5 sm:px-8 lg:px-12 items-center">
          <motion.div
            className="text-center lg:text-left"
            variants={slideInBottom}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.4 }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5 md:mb-6">
              Smart Communication, Perfectly Aligned
            </h2>
            <p className="text-white/80 text-base sm:text-lg lg:text-xl mb-7 md:mb-8 mx-auto lg:mx-0">
              Internal communication is more than sending messages. It’s about
              reaching the right teams at the right time with the right
              information through a single reliable system.
            </p>
          </motion.div>

          {/* VIDEO / IMAGE */}
          <a
            href="https://youtu.be/DO4ECC87KoU"
            target="_blank"
            rel="noopener noreferrer"
            className="block relative group"
          >
            <motion.img
              src={ProductOverviewVideo}
              alt="Watch product demo"
              className="w-full h-auto rounded-xl mx-auto max-w-md lg:max-w-none"
              variants={slideInRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.4 }}
            />

            {/* Animated Play Button */}

            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <motion.div
                className="text-center lg:text-left"
                variants={slideInRight}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.4 }}
              >
                <div className="play-circle">
                  <div className="play-icon"></div>
                </div>
              </motion.div>
            </div>
          </a>
        </div>
      </section>

      {/* ================= DASHBOARD ================= */}
      <section
        className="py-16 md:py-20 bg-black text-white"
        style={{
          backgroundColor: "#FBE3CF",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="container mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          {/* IMAGE */}
          <div className="relative min-h-[300px] sm:min-h-[380px] lg:min-h-[420px] flex items-center justify-center mx-auto lg:mx-0">
            {/* Laptop */}
            <motion.img
              src={LaptopImage}
              alt="Dashboard Laptop"
              className="w-full max-w-[300px] xs:max-w-[360px] sm:max-w-[460px] lg:max-w-xl relative z-10"
              variants={slideInBottom}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.4 }}
            />

            {/* Mobile */}
            <motion.img
              src={MobileImage}
              alt="Dashboard Mobile"
              className="absolute bottom-6 right-6 w-28 xs:w-32 sm:w-40 lg:w-52 z-20 lg:right-[15%] lg:bottom-[18%]"
              initial={{ opacity: 0, scale: 0.8, y: 30 }}
              whileInView={{ opacity: 1, scale: 1, y: 0, x: 80 }}
              transition={{
                delay: 0.4,
                duration: 1.4,
                ease: [0.16, 1, 0.3, 1],
              }}
              viewport={{ once: false, amount: 0.4 }}
            />

            <motion.img
              src={Icons}
              alt="Dashboard Mobile"
              className="hidden sm:block absolute bottom-12 right-[-10px] w-10 lg:w-12 lg:bottom-[80px] lg:right-[-40px] z-20"
              variants={slideInBottom}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.4 }}
            />
          </div>
          <motion.div
            className="text-center lg:text-left"
            variants={fadeIn}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.4 }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl text-brand font-bold mb-4">
              Unified Communication Dashboard
            </h2>
            <p className="text-black text-base sm:text-lg lg:text-xl max-w-xl mx-auto lg:mx-0 mb-6">
              Get a complete overview of internal notifications at a glance.
              Monitor recipients, campaigns, and templates through a single,
              centralized dashboard built for clarity and control.
            </p>

            <ul className="text-black space-y-3 max-w-xl mx-auto lg:mx-0">
              {[
                "Centralized notification tracking",
                "Recipient & department monitoring",
                "Campaign visibility",
                "Real-time status updates",
              ].map((item, i) => (
                <li
                  key={i}
                  className="flex items-center gap-3 justify-center lg:justify-start"
                >
                  <CheckCircle className={`h-5 w-5 ${i % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'}`} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* ================= ALL IN ONE ================= */}

      <section
        className="relative py-16 md:py-20 overflow-hidden"
        style={{
          backgroundColor: "White",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container mx-auto px-5 sm:px-8 lg:px-12 text-center mb-10 md:mb-12 relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold">
            All-in-One Internal
            <br />
            Communication Platform
          </h2>
        </div>

        {/* Left Arrow */}
        <button
          onClick={prevSlide}
          className="hidden md:flex absolute text-white left-4 top-1/2 -translate-y-1/2 z-30 bg-brand hover:bg-white hover:text-black shadow-lg rounded-full w-12 h-12 flex items-center justify-center"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </button>

        {/* Right Arrow */}
        <button
          onClick={nextSlide}
          className="hidden md:flex absolute text-white right-4 top-1/2 -translate-y-1/2 z-30 bg-brand hover:bg-white hover:text-black shadow-lg rounded-full w-12 h-12 flex items-center justify-center"
        >
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>

        {/* CALCULATE WIDTH BASED ON SCREEN */}
        {(() => {
          const cardWidth =
            typeof window !== "undefined"
              ? window.innerWidth < 640
                ? CARD_WIDTH_MOBILE
                : window.innerWidth < 1024
                  ? CARD_WIDTH_TABLET
                  : CARD_WIDTH_DESKTOP
              : CARD_WIDTH_DESKTOP;

          const offset = Math.round(
            activeIndex * (cardWidth + GAP) + cardWidth / 2,
          );

          return (
            <div
              className="relative w-full overflow-hidden"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div
                className="flex gap-6 transition-transform duration-700 ease-in-out mx-auto"
                style={{
                  transform: `translateX(calc(50% - ${offset}px))`,
                }}
              >
                {features.map((item, index) => (
                  <FeatureCard
                    key={index}
                    {...item}
                    active={index === activeIndex}
                  />
                ))}
              </div>
            </div>
          );
        })()}

        <div className="flex justify-center mt-8 gap-3">
          {features.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`h-1 w-6 rounded-full transition-all duration-300 ${
                index === activeIndex ? "bg-brand scale-125" : "bg-muted"
              }`}
            />
          ))}
        </div>
      </section>

      {/* ================= TESTIMONIALS ================= */}
      <section className="py-16 md:py-24 text-white bg-black">
        <div className="container mx-auto px-5 sm:px-8 lg:px-12 text-center mb-10 md:mb-16">
          <h2 className="text-3xl md:text-4xl text-white font-bold">
            What Our Client Say About Us
          </h2>
        </div>

        <motion.div
          className="
    container mx-auto px-5 sm:px-8 lg:px-12
    grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4
    gap-6 sm:gap-8 lg:gap-10
    [perspective:1000px]
  "
          variants={testimonialContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.35 }}
        >
          {testimonials.map((item, i) => (
            <TestimonialCard key={i} {...item} />
          ))}
        </motion.div>
      </section>

      {/* ================= FAQ ================= */}
      <FAQSection />

      <Footer />
    </div>
  );
};

export default CommunicationToolDetails;

function nextSlide() {
  throw new Error("Function not implemented.");
}
function setActiveIndex(arg0: (prev: any) => number) {
  throw new Error("Function not implemented.");
}
